import { Avatar, type AvatarSize } from '@dev-spendesk/grapes';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

import appConfig from 'src/core/config';

export const fallbackSupplierLogoSrc = '/static/img/avatars/supplier.svg';

const MIN_VALID_IMAGE_SIZE = 32;

const validatedUrlCache = new Map<string, string>();

/**
 * Hook that validates supplier logo by checking image dimensions.
 * The API returns a tiny placeholder for unknown suppliers - we detect this
 * by checking if the image is too small and fall back to proper avatar.
 */
export const useValidatedLogoUrl = (
  url: string | undefined,
): string | undefined => {
  const [validatedUrl, setValidatedUrl] = useState<string | undefined>(() => {
    if (url && validatedUrlCache.has(url)) {
      return validatedUrlCache.get(url);
    }
    return undefined;
  });

  useEffect(() => {
    if (!url) {
      setValidatedUrl(undefined);
      return;
    }

    if (validatedUrlCache.has(url)) {
      setValidatedUrl(validatedUrlCache.get(url));
      return;
    }

    const img = new Image();

    const handleLoad = () => {
      if (
        img.naturalWidth < MIN_VALID_IMAGE_SIZE ||
        img.naturalHeight < MIN_VALID_IMAGE_SIZE
      ) {
        validatedUrlCache.set(url, fallbackSupplierLogoSrc);
        setValidatedUrl(fallbackSupplierLogoSrc);
      } else {
        validatedUrlCache.set(url, url);
        setValidatedUrl(url);
      }
    };

    const handleError = () => {
      validatedUrlCache.set(url, fallbackSupplierLogoSrc);
      setValidatedUrl(fallbackSupplierLogoSrc);
    };

    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleError);
    img.src = url;

    return () => {
      img.removeEventListener('load', handleLoad);
      img.removeEventListener('error', handleError);
    };
  }, [url]);

  return validatedUrl;
};

type SupplierLogoProps = {
  /**
   * Text to use to search for the logo using clearbit.
   * If no logo is found, fallback on fallbackSupplierLogoSrc.
   * Used as alt attribute.
   */
  name: string | undefined;
  /**
   * The image URL.
   * If provided, the logo won't be fetched using clearbit
   */
  src?: string;
  /**
   * Specifies the size of the element.
   * @default 'm'
   */
  size?: AvatarSize;
  /**
   * className for the element
   */
  className?: string;
  /**
   * Whether the Avatar src should be lazy loaded
   * @default false
   */
  lazyLoad?: boolean;
};

export const getLogoUrlFromName = (name: string) =>
  `${appConfig.apiUrls.apiV2}/assets/suppliers/logo?name=${encodeURIComponent(name)}`;

export const SupplierLogo = ({
  name,
  src,
  size = 32,
  className,
  lazyLoad = false,
}: SupplierLogoProps) => {
  const logoUrl = name ? (src ?? getLogoUrlFromName(name)) : undefined;
  const validatedUrl = useValidatedLogoUrl(logoUrl);

  return (
    <Avatar
      src={validatedUrl}
      text={name || ''}
      size={size}
      variant="square"
      fallbackSrc={fallbackSupplierLogoSrc}
      className={classNames('shrink-0', className)}
      lazyLoad={lazyLoad}
    />
  );
};
